import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { Icon, type IconName } from '../components/Icon';
import { Sheet } from '../components/Overlays';
import { ROLE_LABELS, initials, useLogout, useMe } from '../lib/session';
import { THEME_CHOICES, THEME_LABELS, type ThemeChoice, useTheme } from '../lib/theme';
import { navCount, useNavCounts } from '../lib/data-api';
import { useIsPhone } from '../lib/use-media';

const LINKS: { label: string; to: string; icon: IconName; key?: string }[] = [
  { label: 'Início', to: '/', icon: 'home' },
  { label: 'Orçamentos', to: '/orcamentos', icon: 'quote', key: 'orcamentos' },
  { label: 'Clientes', to: '/clientes', icon: 'users' },
  { label: 'Impressoras', to: '/impressoras', icon: 'printer' },
  { label: 'Enviar fotos e vídeos', to: '/midia/enviar', icon: 'image' },
  { label: 'Despesas', to: '/despesas', icon: 'card' },
];

const THEME_SUB: Record<ThemeChoice, string> = {
  auto: 'segue o celular',
  light: '',
  dark: 'bom à noite na oficina',
};

/** Phone "Mais" screen (design/Shell-Celular.dc.html). */
export function MorePage() {
  const phone = useIsPhone();
  const me = useMe();
  const { choice, setChoice, dark } = useTheme();
  const logout = useLogout();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState<'theme' | 'logout' | null>(null);
  const counts = useNavCounts();

  if (!phone) return <Navigate to="/" replace />;

  return (
    <>
      <div className="card row" style={{ gap: 12, padding: '14px 16px' }}>
        <span
          className={dark ? 'avatar co' : 'avatar'}
          style={{ width: 44, height: 44, flexBasis: 44, fontSize: 17 }}
        >
          {initials(me.name)}
        </span>
        <div className="grow" style={{ minWidth: 0 }}>
          <p style={{ fontWeight: 600 }}>{me.name}</p>
          <p className="sm muted trunc">
            {ROLE_LABELS[me.role]} · {me.email}
          </p>
        </div>
      </div>
      <div className="card" style={{ padding: '4px 16px' }}>
        {LINKS.map((l) => (
          <Link key={l.to} className="listrow" to={l.to}>
            <Icon name={l.icon} />
            <span className="grow">{l.label}</span>
            {l.key && navCount(counts.data, l.key) ? (
              <span className="count">{navCount(counts.data, l.key)}</span>
            ) : null}
          </Link>
        ))}
      </div>
      <div className="card" style={{ padding: '4px 16px' }}>
        <button
          type="button"
          className="listrow"
          style={{ width: '100%' }}
          onClick={() => setSheet('theme')}
          aria-haspopup="dialog"
        >
          <Icon name="moon" />
          <span className="grow">Tema</span>
          <span className="sm muted">{THEME_LABELS[choice]}</span>
          <Icon name="chevronRight" />
        </button>
        <Link className="listrow" to="/configuracoes">
          <Icon name="gear" />
          <span className="grow">Configurações</span>
          <span className="xs muted">melhor no computador</span>
        </Link>
        <button
          type="button"
          className="listrow"
          style={{ width: '100%', color: 'var(--err)' }}
          onClick={() => setSheet('logout')}
          aria-haspopup="dialog"
        >
          <Icon name="logout" />
          <span className="grow">Sair</span>
        </button>
      </div>

      {sheet === 'theme' ? (
        <Sheet label="Tema" onClose={() => setSheet(null)}>
          <h2>Tema</h2>
          <p className="sm muted">Automático segue o modo claro ou escuro do celular.</p>
          <div role="radiogroup" aria-label="Tema">
            {THEME_CHOICES.map((t) => (
              <button
                key={t}
                type="button"
                className="listrow"
                style={{ width: '100%' }}
                role="radio"
                aria-checked={choice === t}
                onClick={() => {
                  setChoice(t);
                  setSheet(null);
                }}
              >
                <span className="grow" style={{ fontWeight: 500 }}>
                  {THEME_LABELS[t]}
                </span>
                <span className="xs muted">{THEME_SUB[t]}</span>
                <span className={choice === t ? 'box on' : 'box'} style={{ borderRadius: '50%' }}>
                  {choice === t ? <Icon name="check" size={14} strokeWidth={3} /> : null}
                </span>
              </button>
            ))}
          </div>
        </Sheet>
      ) : null}

      {sheet === 'logout' ? (
        <Sheet label="Sair da conta" onClose={() => setSheet(null)}>
          <h2>Sair da conta?</h2>
          <p className="sm muted">
            Você precisa entrar de novo neste celular. Nada do que está salvo se perde.
          </p>
          <button
            type="button"
            className="btn d block"
            onClick={() => logout.mutate(undefined, { onSettled: () => void navigate('/login') })}
          >
            Sair
          </button>
          <button type="button" className="btn block" onClick={() => setSheet(null)}>
            Cancelar
          </button>
        </Sheet>
      ) : null}
    </>
  );
}
